import { useCallback, useState } from 'react';
import { useContextAwareDownloadProjectReport, useContextAwareLazyGetFastActivitiesByProject } from '@hooks/index';
import { useTriggerAsyncReportMutation } from '@app/features/project/projectApiSlice';
import { useAppSelector } from '@app/hooks';
import { extractErrorMessage } from '@utils/useErrorMessage';
import { isFetchBaseQueryError } from '@utils/helperFunctions';
import type { ErrorType, FastActivity } from '@typedefs/interfaces';
import type { ReportAsyncResponse } from '@app/features/asyncJobs/asyncJobsTypes';
import i18n from 'src/i18n';

const ASYNC_THRESHOLD = 40;

interface UseDownloadReportProps {
  projectId: number;
  activities?: number[];
}

interface UseDownloadReportReturn {
  downloadExcel: () => Promise<void>;
  downloadNarrativeReport: () => Promise<void>;
  isLoading: boolean;
  errorMsg: string | null;
  isOpen: boolean;
  handleCancel: () => void;
  emailNoticeOpen: boolean;
  closeEmailNotice: () => void;
  readinessOpen: boolean;
  notReadyActivities: FastActivity[];
  closeReadinessModal: () => void;
}

const useDownloadReport = ({ projectId, activities }: UseDownloadReportProps): UseDownloadReportReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [emailNoticeOpen, setEmailNoticeOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [notReadyActivities, setNotReadyActivities] = useState<FastActivity[]>([]);
  
  const [triggerSyncDownload] = useContextAwareDownloadProjectReport();
  const [fetchNotReadyActivities] = useContextAwareLazyGetFastActivitiesByProject();
  const [triggerAsyncReport] = useTriggerAsyncReportMutation();
  
  const project = useAppSelector(state => 
    state.api?.queries?.[`getProjectById("${projectId}")`]?.data as { activity_count?: string; module_count?: string } | undefined
  );

  const handleCancel = () => {
    setIsOpen(false);
    setErrorMsg(null);
  };

  const closeEmailNotice = () => {
    setEmailNoticeOpen(false);
  };

  const closeReadinessModal = () => {
    setReadinessOpen(false);
    setNotReadyActivities([]);
  };

  const isSmallProject = useCallback(() => {
    if (!project) return true;
    
    const activityCount = parseInt(project.activity_count || '0', 10);
    const moduleCount = parseInt(project.module_count || '0', 10);
    return activityCount + moduleCount <= ASYNC_THRESHOLD;
  }, [project]);

  const handleDownload = useCallback(async (options: {
    template?: string;
    fileName: string;
  }) => {
    const { template, fileName } = options;
    
    if (!projectId) return;
    setIsLoading(true);
    setErrorMsg(null);
    setEmailNoticeOpen(false);
    setReadinessOpen(false);
    setNotReadyActivities([]);
    
    try {
      try {
        const response = await fetchNotReadyActivities({
          projectId,
          ready: false,
          page: 1,
          pageSize: 100,
        }).unwrap();

        const selectedActivityIds = activities ? new Set(activities) : null;
        // The public activities endpoint ignores `ready`, so re-check completion here.
        const includedNotReadyActivities = response.results.filter(
          activity => activity.completion_percentage < 1
            && !activity.is_b_intact
            && (!selectedActivityIds || selectedActivityIds.has(activity.id))
        );

        if (includedNotReadyActivities.length > 0) {
          setNotReadyActivities(includedNotReadyActivities);
          setReadinessOpen(true);
          return;
        }
      } catch {
        // The report endpoint also validates readiness, so continue when the pre-check fails.
      }

      if (isSmallProject()) {
        const response = await triggerSyncDownload({
          id: projectId,
          activities,
          ...(template && { template }),
          currentLanguage: i18n.language,
        });

        if ('error' in response) {
          if (isFetchBaseQueryError(response.error) && response.error.data instanceof Blob) {
            const errorText = await response.error.data.text();
            const errorJson = JSON.parse(errorText);
            setErrorMsg(errorJson.details || 'An error occurred while downloading the report');
          } else {
            const errMsg = extractErrorMessage(response.error as ErrorType);
            setErrorMsg(errMsg || 'An error occurred while downloading the report');
          }
          setIsOpen(true);
          return;
        }

        const { data: blob } = response;
        if (!blob) {
          setErrorMsg('An error occurred while downloading the report');
          setIsOpen(true);
          return;
        }

        const url = window.URL.createObjectURL(blob as Blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        await triggerAsyncReport({
          id: projectId,
          activities,
          ...(template && { template }),
          currentLanguage: i18n.language,
        }).unwrap() as ReportAsyncResponse;

        setEmailNoticeOpen(true);
      }
    } catch (error) {
      console.error('Error downloading the report:', error);
      setErrorMsg('An unexpected error occurred while downloading the report');
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, activities, fetchNotReadyActivities, isSmallProject, triggerSyncDownload, triggerAsyncReport]);

  const downloadExcel = useCallback(() => {
    return handleDownload({
      fileName: `${projectId}-results-report.xlsx`,
    });
  }, [handleDownload, projectId]);

  const downloadNarrativeReport = useCallback(() => {
    return handleDownload({
      template: 'fao',
      fileName: `${projectId}-narrative-report.pdf`,
    });
  }, [handleDownload, projectId]);

  return {
    isLoading,
    downloadExcel,
    downloadNarrativeReport,
    handleCancel,
    isOpen,
    errorMsg,
    emailNoticeOpen,
    closeEmailNotice,
    readinessOpen,
    notReadyActivities,
    closeReadinessModal,
  };
};

export default useDownloadReport;
